import { ApiError } from "@/services/fobizzToolsApi";
import * as Sentry from "@sentry/vue";
import { AxiosError, isAxiosError, isCancel } from "axios";
import type { App } from "vue";
import type { Router } from "vue-router";

interface ExtendedAxiosError extends AxiosError {
  code?: string;
  message: string;
}

// has user cancelled request (navigated away, etc.)
// ideas: https://github.com/axios/axios/issues/6209
const isNavCancel = (e: AxiosError) =>
  isCancel(e) ||
  (e as ExtendedAxiosError).code === "ERR_CANCELED" ||
  ((e as ExtendedAxiosError).code === "ECONNABORTED" &&
    /aborted|canceled/i.test((e as ExtendedAxiosError).message));

// only report unexpected axios errors
const shouldReportAxios = (e: AxiosError) => {
  if (isNavCancel(e)) return false;
  if (!e.response) return false;
  const s = e.response.status;

  // only report errors with status >= 500
  return s >= 500;
};

// Check if the error is an ApiError and determine if it should be reported
const isApiError = (err: unknown): err is ApiError => {
  return (
    typeof err === "object" &&
    err !== null &&
    "name" in err &&
    (err as { name?: string }).name === "ApiError" &&
    "status" in err
  );
};

// only report ApiError instances with status >= 500
const shouldReportApiError = (err: ApiError): boolean => {
  return err.status >= 500;
};

// drops stale-chunk errors
const isChunkLoadError = (err: unknown): boolean => {
  const msg = (err as Error)?.message ?? "";
  return (
    /(Couldn't resolve component)/i.test(msg) ||
    /(Failed to fetch dynamically imported module)/i.test(msg) ||
    /(error loading dynamically imported module)/i.test(msg) ||
    /(Importing a module script failed)/i.test(msg) ||
    /(Unable to preload CSS for)/i.test(msg)
  );
};

// drops abort errors
const isAbortDomException = (err: unknown): boolean =>
  err instanceof DOMException && err.name === "AbortError";

// filter out browser extensions
const denyUrls = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /^ms-browser-extension:/i,
];

// https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-thirdpartyerrorfilterintegration
export function initSentry(app: App, router: Router) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    denyUrls,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.extraErrorDataIntegration({ depth: 4 }),
    ],
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    tracePropagationTargets: ["localhost", /^https:\/\/app\.fobizz\.com\/api/],
    beforeSend(event, hint) {
      const err = hint?.originalException;

      if (isChunkLoadError(err) || isAbortDomException(err)) return null;

      if (isAxiosError(err)) {
        if (!shouldReportAxios(err)) return null;
      }

      if (isApiError(err)) {
        if (!shouldReportApiError(err)) return null;
      }

      return event;
    },
  });
}
